import React, { useContext, useState } from 'react';
import { Settings, Shield, MapPin, RefreshCw, ChevronRight, MessageSquare, ExternalLink, Mail, Phone, Lock } from 'lucide-react';
import { AppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import PostCard from '../components/PostCard';
import { cn } from '../utils';

export default function Profile() {
  const { currentUser, logout, token, myPosts, updateProfile } = useContext(AppContext);
  const navigate = useNavigate();

  if (!token || !currentUser) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="text-center space-y-6">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-900 rounded-3xl flex items-center justify-center mx-auto">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-2xl font-black tracking-tight">Identity Locked</h2>
          <button 
            onClick={() => navigate('/login')} 
            className="px-8 py-3 bg-slate-900 dark:bg-white text-white dark:text-black rounded-2xl font-bold active:scale-95 transition-all"
          >
            Authenticate
          </button>
        </div>
      </div>
    );
  }

  const handleRandomizeAvatar = () => {
    const newSeed = Math.random().toString(36).substring(7);
    updateProfile({ avatar_seed: newSeed });
  };

  const isAdmin = currentUser.isAdmin;

  return (
    <div className="max-w-xl mx-auto px-6 py-12 md:py-20 min-h-screen">
      {/* Profile Info */}
      <div className="flex flex-col items-center text-center space-y-6 mb-16">
        <div className="relative">
          <div className={cn(
            "p-1.5 rounded-[2.5rem] transition-all duration-500",
            isAdmin ? "bg-gradient-to-tr from-amber-400 to-fuchsia-500 shadow-[0_0_30px_rgba(245,158,11,0.2)]" : "bg-slate-100 dark:bg-slate-900"
          )}>
            <img 
              src={currentUser.avatarUrl} 
              alt="Avatar" 
              className="w-32 h-32 rounded-[2.3rem] border-4 border-white dark:border-black bg-white dark:bg-black"
            />
          </div>
          <button 
            onClick={handleRandomizeAvatar}
            className="absolute -bottom-2 -right-2 p-2.5 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 hover:scale-110 active:rotate-180 transition-all"
          >
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="space-y-1">
          <h1 className="text-4xl font-black tracking-tighter text-slate-900 dark:text-white">
            {currentUser.username}
          </h1>
          <div className="flex items-center justify-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
              {isAdmin ? "System Administrator" : "Anonymous Explorer"}
            </span>
            {isAdmin && <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></div>}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-white/5 rounded-full text-[11px] font-bold text-slate-500">
            <MapPin className="w-3.5 h-3.5" />
            {currentUser.university || "Global"}
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-white/5 rounded-full text-[11px] font-bold text-slate-500">
            <Shield className="w-3.5 h-3.5 text-indigo-500" />
            Verified
          </div>
        </div>
      </div>

      {/* Aesthetic Stats Bar */}
      <div className="flex items-center justify-center gap-12 mb-16 px-4">
        <div className="text-center group cursor-pointer">
          <div className="text-3xl font-black text-slate-900 dark:text-white group-hover:scale-110 transition-transform">
            {currentUser.stats.posts}
          </div>
          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Posts</div>
        </div>
        <div className="w-px h-8 bg-slate-100 dark:bg-slate-900"></div>
        <div className="text-center group cursor-pointer">
          <div className="text-3xl font-black text-slate-900 dark:text-white group-hover:scale-110 transition-transform">
            {currentUser.stats.reactions}
          </div>
          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Impact</div>
        </div>
      </div>

      {/* Identity Cards - Clean & Simple */}
      <div className="space-y-4 mb-16">
        <div className="group p-6 bg-slate-50/50 dark:bg-white/5 rounded-[2rem] border border-transparent hover:border-slate-100 dark:hover:border-slate-800 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Digital Contact</div>
            <Mail className="w-4 h-4 text-slate-300" />
          </div>
          <div className="text-lg font-bold text-slate-800 dark:text-slate-200">{currentUser.email}</div>
        </div>
        
        {currentUser.phone && (
          <div className="group p-6 bg-slate-50/50 dark:bg-white/5 rounded-[2rem] border border-transparent hover:border-slate-100 dark:hover:border-slate-800 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Phone Link</div>
              <Phone className="w-4 h-4 text-slate-300" />
            </div>
            <div className="text-lg font-bold text-slate-800 dark:text-slate-200">{currentUser.phone}</div>
          </div>
        )}
      </div>

      {/* Posts Section */}
      <div className="space-y-8">
        <div className="flex items-center justify-between px-2">
          <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.3em]">Timeline</h3>
          <div className="text-[10px] font-bold text-indigo-500">View All</div>
        </div>
        
        {myPosts.length === 0 ? (
          <div className="py-20 text-center space-y-4">
            <MessageSquare className="w-12 h-12 text-slate-100 dark:text-slate-900 mx-auto" />
            <p className="text-slate-400 text-sm font-medium italic">Nothing here but ghosts...</p>
          </div>
        ) : (
          <div className="space-y-2">
            {myPosts.map(post => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        )}
      </div>

      {/* Minimal Footer Actions */}
      <div className="mt-20 pt-10 border-t border-slate-100 dark:border-slate-900 flex flex-col items-center space-y-8">
        <div className="flex items-center gap-8">
          <button className="text-[11px] font-black text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors tracking-widest">SETTINGS</button>
          <button className="text-[11px] font-black text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors tracking-widest">PRIVACY</button>
          <button className="text-[11px] font-black text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors tracking-widest">HELP</button>
        </div>
        <button 
          onClick={() => { logout(); navigate('/'); }}
          className="px-10 py-4 bg-red-500 text-white rounded-2xl font-black text-xs tracking-[0.2em] shadow-xl shadow-red-500/20 active:scale-95 transition-all"
        >
          TERMINATE SESSION
        </button>
      </div>
    </div>
  );
}
